import React, { FC, useState } from 'react';
import { useSelector } from 'react-redux';
import { Form, Formik, FormikHelpers } from 'formik';
import { ProfileService } from 'services/profile/profile';
import { validationSchema } from './form';
import { CompanyProfileReqBody } from 'core/interface/matchProfile';
import useLocales from 'hooks/useLocales';
import Autocomplete from 'components/shared/Autocomplete/Index';
import FormField from 'components/FormField/Index';
import { InputType } from 'core/enum/inputType';
import Button from 'components/Button/Index';
import { JobService } from 'services/jobs/job';
import { AuthService } from 'services/auth/auth';
import { RootState, dispatch } from '../../../../redux/store';
import { SaveCompanies } from '../../../../redux/slices/company';
import { IMe } from 'core/interface/me';
import DropDown from 'components/Dropdown/index';
import { companySizeChoices } from 'core/constants/aboutMePopUpConstants';
import { JobCitiesResultsType } from 'core/interface/job';

import './Index.scss';
import Popup from '../Popup/Index';

const CompanyInfoPopup = ({ onSubmit, onClose }: any): JSX.Element => {
  const { companies } = useSelector((state: RootState) => state.company);
  const { t } = useLocales();
  const me = useSelector((state: IMe) => state.user).user;

  const { industry: industries } = useSelector(
    (state: RootState) => state.industries
  );

  const { cities } = useSelector((state: RootState) => state.locations);

  const getIndustries = async (query?: string): Promise<void> => {
    ProfileService.getIndustries(query);
  };

  const handleSubmit = async (
    values: CompanyProfileReqBody,
    actions: FormikHelpers<CompanyProfileReqBody>
  ): Promise<void> => {
    // Format website URL to include https:// if not already present
    const formattedValues = { ...values };
    if (formattedValues.website) {
      formattedValues.website = formattedValues.website.match(
        /^(https?|ftp):\/\//
      )
        ? formattedValues.website
        : `https://${formattedValues.website}`;
    }

    // Create request body with location as an object
    const requestBody: Record<string, any> = {};

    // Copy all fields except location and size
    Object.keys(formattedValues).forEach((key) => {
      if (key !== 'location' && key !== 'size' && key !== 'logo') {
        requestBody[key] = (formattedValues as Record<string, any>)[key];
      }
    });

    // Handle location separately
    if (formattedValues.location) {
      requestBody.location = {
        id: formattedValues.location.id,
        display_name: formattedValues.location.display_name
      };
    }

    // Handle size separately
    if (formattedValues.size) {
      requestBody.size = formattedValues.size;
    }

    try {
      const res = await AuthService.getAccountCompany(me.company, requestBody);

      if (res) {
        dispatch(SaveCompanies(res));
        onSubmit();
      }
    } catch (error) {
      console.log(error);
    }
  };

  const getCities = async (q: string, limit: number = 10) => {
    JobService.getLocations({
      limit,
      q
    });
  };

  const handleLoadMoreCities = () => {
    const params = { limit: 10, offset: cities.length };
    JobService.getLocations(params, true);
  };

  const handleLocationChange = (value: string, setFieldValue: any) => {
    if (!value) {
      setFieldValue('location', null);
      return;
    }

    // Find the city from the selected city name
    const selectedCity = cities?.find(
      (city: JobCitiesResultsType) => city.display_name === value
    );
    if (selectedCity) {
      setFieldValue('location', {
        id: selectedCity.id,
        display_name: selectedCity.display_name
      });
    }
  };

  return (
    <Popup
      onClose={onClose}
      title={t('Nice to meet you')}
      description="Please tell us a little bit more about your company"
    >
      <div className="companyInfoPopup">
        <Formik
          enableReinitialize
          initialValues={{
            ...companies,
            location: companies?.location || null,
            size: companies?.size || null
          }}
          onSubmit={handleSubmit}
          validationSchema={validationSchema(t)}
        >
          {({ setFieldValue, isValid, values, errors, touched }) => {
            return (
              <Form>
                {/* 1. Company name */}
                <div className="field">
                  <span className="label">{t('Company name')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="name"
                    placeholder={t('Company name')}
                    type={InputType.text}
                  />
                </div>
                {/* 2. Company website (url) */}
                <div className="field">
                  <span className="label">{t('Company website (url)')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="website"
                    placeholder="https://example.com"
                    type={InputType.text}
                  />
                </div>
                {/* 3. Primary contact name */}
                <div className="field">
                  <span className="label">{t('Primary contact name')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="contact_name"
                    placeholder={t('Contact name')}
                    type={InputType.text}
                  />
                </div>
                {/* 4. Primary contact email */}
                <div className="field">
                  <span className="label">{t('Primary contact email')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="contact_email"
                    placeholder="contact@email.com"
                    type={InputType.email}
                  />
                </div>
                {/* 5. Primary contact job title */}
                <div className="field">
                  <span className="label">
                    {t('Primary contact job title')}
                  </span>
                  <FormField
                    inputClassName="input"
                    fieldName="contact_job_title"
                    placeholder={t('Job title')}
                    type={InputType.text}
                  />
                </div>
                {/* 6. Billing email */}
                <div className="field">
                  <span className="label">{t('Billing email')}</span>
                  <FormField
                    inputClassName="input"
                    fieldName="billing_email"
                    placeholder="billing@email.com"
                    type={InputType.email}
                  />
                </div>
                {/* 7. Official company language */}
                <div className="field">
                  <span className="label">
                    {t('Official company language')}
                  </span>
                  <DropDown
                    items={[
                      { value: 'en', to_show: 'English' },
                      { value: 'nl', to_show: 'Dutch' }
                    ]}
                    fieldName="lang"
                    selectedValue={(val: string) => setFieldValue('lang', val)}
                    defaultValue={values.lang}
                    placeholder={t('Select language')}
                  />
                </div>
                {/* 8. Industry */}
                <div className="field">
                  <span className="label">{t('Industry')}</span>
                  <Autocomplete
                    className="auto"
                    fieldName="industry"
                    value={
                      industries?.find(
                        (industry) =>
                          String(industry?.id) === String(values.industry)
                      )?.name ?? ''
                    }
                    options={
                      industries?.map((e) => ({
                        id: e?.id,
                        title: e?.name
                      })) || []
                    }
                    show_location={false}
                    placeholder={t('Industry')}
                    onChange={(e) => {
                      const selectedIndustry = industries?.find(
                        (industry) => industry.name === e
                      );
                      if (selectedIndustry) {
                        setFieldValue('industry', selectedIndustry.id);
                      }
                    }}
                    onInputChange={(e) => getIndustries(e)}
                    values={industries}
                    onFocus={(e) => {
                      getIndustries(e);
                    }}
                  />
                </div>
                {/* 9. Company location */}
                <div className="field">
                  <span className="label">{t('In which city is your company located?')}</span>
                  <Autocomplete
                    fieldName="location"
                    className="auto"
                    value={values.location?.display_name || ''}
                    options={
                      cities.map((e: any) => ({
                        id: e?.id,
                        title: e?.display_name
                      })) || []
                    }
                    show_location={true}
                    placeholder={t("Enter a city name (e.g., 'Paris') to search…")}
                    onChange={(e) => {
                      handleLocationChange(e, setFieldValue);
                    }}
                    onInputChange={(e) => getCities(e)}
                    values={cities}
                    onFocus={(e) => getCities(e)}
                    loadMore={handleLoadMoreCities}
                  />
                </div>
                {/* 10. Company size */}
                <div className="field">
                  <span className="label">{t('Company size')}</span>
                  <DropDown
                    dropdownClassName="company_size_dropdown"
                    items={companySizeChoices}
                    fieldName="size"
                    selectedValue={(val: string) =>
                      setFieldValue('size', parseInt(val))
                    }
                    defaultValue={values.size?.toString()}
                    placeholder={t('Select company size')}
                  />
                </div>
                <Button
                  type="submit"
                  t={t}
                  title="Save"
                  className={`${isValid ? 'valid' : 'invalid'} save`}
                />
                {!isValid && (
                  <div style={{ color: 'red', marginTop: '10px' }}>
                    Form has validation errors. Check required fields.
                  </div>
                )}
              </Form>
            );
          }}
        </Formik>
      </div>
    </Popup>
  );
};

export default CompanyInfoPopup;
